import { useAuth0 } from '@auth0/auth0-react'
import { Menu, MenuItem } from '@rgrd/component-library'

import { useRegardAuth } from '~/shell/auth/useRegardAuth'

import styles from './UserMenu.module.css'

/**
 * Derives up-to-two-letter initials from an Auth0 profile, falling back to a
 * neutral glyph when no usable name or email is present.
 */
const getInitials = (name?: string, email?: string): string => {
  const source = name?.trim() || email?.trim()
  if (!source) {
    return '—'
  }

  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

/**
 * The header avatar and its user menu.
 *
 * The avatar shows the signed-in user's initials. It is an interactive dropdown
 * only when there is at least one action to offer — today that is **Logout**,
 * gated by `useRegardAuth().canLogout` (meaningful in standalone mode, hidden
 * when the EHR owns the session). With no actions the avatar is a plain,
 * non-interactive initials circle (see the **User menu** glossary entry in
 * `GLOSSARY.md`).
 */
export const UserMenu = () => {
  const { user } = useAuth0()
  const { canLogout, logout } = useRegardAuth()
  const initials = getInitials(user?.name, user?.email)

  if (!canLogout) {
    return (
      <span className={styles.avatar} aria-hidden="true">
        {initials}
      </span>
    )
  }

  return (
    <Menu
      label="Account"
      triggerLabel="Account"
      align="end"
      triggerClassName={styles.trigger}
      trigger={() => (
        <span className={styles.avatar} aria-hidden="true">
          {initials}
        </span>
      )}
    >
      <MenuItem onSelect={logout}>Log out</MenuItem>
    </Menu>
  )
}
